import Pause from "@nishtech/MobiusOverrides/Icons/Pause";
import Play from "@nishtech/MobiusOverrides/Icons/Play";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@nishtech/Store/Nishtech/Links/OldLinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import useEmblaCarousel from "embla-carousel-react";
import parse from "html-react-parser";
import React, { FC, useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

function getVideoEmbed(src: string | undefined): { type: "youtube" | "vimeo" | "mp4" | "unknown"; src: string } {
    if (!src) {
        return { type: "unknown", src: "" };
    }

    const url = src.trim();

    const ytShort = url.match(/youtu\.be\/([A-Za-z0-9_-]{5,})/);
    if (ytShort) {
        return { type: "youtube", src: `https://www.youtube-nocookie.com/embed/${ytShort[1]}` };
    }

    const ytWatch = url.match(/[?&]v=([A-Za-z0-9_-]{5,})/);
    if (ytWatch) {
        return { type: "youtube", src: `https://www.youtube-nocookie.com/embed/${ytWatch[1]}` };
    }

    const isYtEmbed = url.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{5,})/);
    if (isYtEmbed) {
        return { type: "youtube", src: url };
    }

    const vimeoShort = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoShort) {
        return { type: "vimeo", src: `https://player.vimeo.com/video/${vimeoShort[1]}` };
    }

    const vimeoEmbed = url.match(/player\.vimeo\.com\/video\/(\d+)/);
    if (vimeoEmbed) {
        return { type: "vimeo", src: url };
    }

    const mp4 = url.match(/\.mp4($|\?)/i);
    if (mp4) {
        return { type: "mp4", src: url };
    }

    return { type: "unknown", src: url };
}

interface SlideModel {
    fields: {
        slideTitle: string;
        background: "image" | "color" | "video";
        image: string;
        imageMobile: string;
        imageOverlay: string;
        backgroundVideoURL: string;
        partialOverlay: boolean;
        partialOverlayPositioning: "top" | "middle" | "bottom";
        responsiveImageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
        backgroundColor: string;
        heading: string;
        headingMobile: string;
        headingColor: string;
        headingFontSize: number;
        mobileHeadingFontSize: number;
        mobileSubHeadingFontSize: number;
        headingMobileColor: string;
        subheading: string;
        subheadingMobile: string;
        subheadingColor: string;
        subHeadingFontSize: number;
        subheadingMobileColor: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary";
        marginTop: number;
        marginLeft: number;
        contentAlignment: "left" | "right";
        centerTextVertically: boolean;
        customCSS: string;
    };
}

const enum fields {
    textAlignment = "textAlignment",
    slideHeight = "slideHeight",
    showArrows = "showArrows",
    arrowColor = "arrowColor",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    pausable = "pausable",
    slides = "slides",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
    headingFontSize = "headingFontSize",
    subHeadingFontSize = "subHeadingFontSize",
    mobileHeadingFontSize = "mobileHeadingFontSize",
    mobileSubHeadingFontSize = "mobileSubHeadingFontSize",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.slideHeight]: string;
        [fields.showArrows]: boolean;
        [fields.arrowColor]: string;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.pausable]: boolean;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.slides]: SlideModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    pausePlayDotButton?: ButtonPresentationProps;
    pausePlayDotButtonIcon?: InjectableCss;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    asHeroSection: InjectableCss;
}

export const slideshowStyles: SlideshowStyles = {
    asHeroSection: {
        css: css`
            position: relative;
            height: 100%;

            .slick-slider {
                -webkit-touch-callout: none;
                -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
                box-sizing: border-box;
                touch-action: pan-y;
                -webkit-user-select: none;
                -moz-user-select: none;
                user-select: none;
            }
            .slick-list,
            .slick-slider {
                display: block;
                position: relative;
            }
            .slick-list {
                margin: 0;
                overflow: hidden;
                padding: 0;
            }
            .slick-list:focus {
                outline: none;
            }
            .slick-slider .slick-list,
            .slick-slider .slick-track {
                transform: translateZ(0);
            }
            .slick-track {
                display: block;
                left: 0;
                position: relative;
                top: 0;
            }
            .slick-track:after,
            .slick-track:before {
                content: "";
                display: table;
            }
            .slick-track:after {
                clear: both;
            }
            .slick-slide {
                display: none;
                float: left;
                height: 100%;
                min-height: 1px;
            }
            .slick-slide img {
                display: block;
            }
            .slick-initialized .slick-slide {
                display: block;
            }
            .slick-arrow {
                background-color: transparent;
                background-image: url("/staticContent/svg/mobileArrow.svg");
                background-repeat: no-repeat;
                background-size: 100%;
                border: 0;
                cursor: pointer;
                font-size: 0;
                height: 30px;
                padding: 0;
                position: absolute;
                right: 0;
                top: 50%;
                transform: translateY(-50%);
                transition: opacity 0.3s ease;
                width: 15px;
                z-index: 10;
            }
            .slick-arrow.slick-prev {
                left: 10px;
                top: 15px;
                transform: rotateY(180deg) translateY(-50%);
            }
            .slick-arrow.slick-next {
                right: 10px;
                top: 15px;
            }
            .slick-arrow:hover {
                opacity: 0.8;
            }
            *,
            :after,
            :before {
                box-sizing: inherit;
            }
            body {
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
                color: #363936;
                font-family: "Inter Tight", Arial;
                font-size: 18px;
                font-weight: 500;
                line-height: 1.4;
            }
            a {
                color: ${getColor("primary.main")};
                transition: color 0.35s cubic-bezier(0.3, 0.86, 0.36, 0.95);
            }
            a,
            a:hover {
                -webkit-text-decoration: none;
                text-decoration: none;
            }
            a:hover {
                color: #c8102e;
            }
            p {
                margin: 0 0 22px;
                font-size: 18px;
            }
            h1 {
                font-weight: inherit;
                margin: 0 0 10px;
            }
            h1 {
                font-size: 28px;
                font-weight: 500;
                letter-spacing: 0.84px;
                line-height: 1.25;
            }
            body {
                background-color: #fff;
            }
            .as-wrapper {
                overflow: hidden;
                position: relative;
                width: 100%;
            }
            .as-container:after {
                clear: both;
                content: "";
                display: block;
            }
            .as-container {
                margin: 0 auto;
                max-width: 1180px;
                padding-left: 20px;
                padding-right: 20px;
                width: 100%;
            }
            .as-btn {
                display: inline-block;
                font-size: 16px;
                letter-spacing: 0.48px;
                line-height: 1.2;
                transition:
                    background 0.3s ease,
                    color 0.3s ease;
                vertical-align: top;
            }
            .as-btn-primary {
                border-radius: 10px;
                font-weight: 700;
            }
            .as-hero-section {
                position: relative;
            }
            .as-hero-section .as-slide {
                height: 572px;
                overflow: hidden;
                padding: 0 30px;
                position: relative;
            }
            .as-hero-section .as-container {
                height: 100%;
                max-width: 1310px;
                position: relative;
                z-index: 2;
            }
            .as-hero-section p {
                margin-bottom: 15px;
            }
            .as-hero-section .as-text-area {
                align-items: flex-end;
                color: #fff;
                display: flex;
                height: 100%;
                letter-spacing: 0.18px;
                margin: 0 -80px;
                padding-bottom: 0;
                padding-top: 20px;
                position: relative;
                text-align: center;
                z-index: 1;
            }
            .as-hero-section .as-text-box {
                background: rgb(4 35 89 / 80%);
                margin: 0;
                padding: 36px 50px 47px;
                width: 100%;
            }
            .as-hero-section h1 {
                color: #fff;
                margin-bottom: 6px;
            }
            .as-hero-section .slick-arrow.slick-prev {
                left: 15px;
            }
            .as-hero-section .slick-arrow.slick-next {
                right: 15px;
            }
            .as-bg-image {
                bottom: 0;
                left: 0;
                position: absolute;
                right: 0;
                top: 0;
                z-index: -2;
            }
            .as-bg-image img {
                height: 100%;
                -o-object-fit: cover;
                object-fit: cover;
                vertical-align: top;
                width: 100%;
            }
            .as-bg-image.as-bg-image-mob {
                z-index: -1;
            }

            .slideVideoScale {
                transform: scale(1);
                pointer-events: auto;
                width: 100%;
                height: 100%;
                object-fit: cover;
            }

            @media (min-width: 768px) {
                .as-hidden-md {
                    display: none;
                }
                .as-hero-section .as-slide {
                    height: 450px;
                }
                .as-hero-section .as-slide.text-right .as-text-area {
                    margin: 0 0 0 auto;
                    text-align: right;
                }
                .as-hero-section .as-slide.text-right .as-container:after {
                    left: 30%;
                    right: 0;
                }
                .as-hero-section .as-slide.text-right .as-container:before {
                    left: 100%;
                    right: auto;
                }
                .as-hero-section .as-slide.text-center .as-text-area {
                    margin: 0 auto;
                    text-align: center;
                }
                .as-hero-section .as-slide.text-center .as-container:after {
                    left: 0;
                    right: 0;
                }
                .as-hero-section .as-slide.text-center .as-container:before {
                    display: none;
                }
                .as-hero-section .as-container:after {
                    bottom: 0;
                    content: "";
                    left: 0;
                    position: absolute;
                    right: 30%;
                    top: 0;
                    z-index: -1;
                }
                .as-hero-section .as-container:before {
                    bottom: 0;
                    content: "";
                    position: absolute;
                    right: 100%;
                    top: 0;
                    width: 100vw;
                }
                .as-hero-section .as-text-area {
                    align-items: center;
                    color: #013d54;
                    margin: 0;
                    max-width: none;
                    padding-bottom: 85px;
                    padding-top: 50px;
                    text-align: left;
                }
                .as-hero-section .as-text-box {
                    background-color: transparent;
                    margin: 0;
                    padding: 0;
                }
                .as-hero-section h1 {
                    color: #013d54;
                    margin-bottom: 10px;
                }
                .as-hero-section .slick-arrow.slick-next,
                .as-hero-section .slick-arrow.slick-prev {
                    margin-top: -20px;
                }
                .as-bg-image.as-bg-image-mob {
                    display: none;
                }
            }
            @media (min-width: 1024px) {
                .slick-arrow {
                    background-image: url("/staticContent/svg/deskTopArrow.svg");
                    height: 38px;
                    width: 21px;
                }
                h1 {
                    font-size: 32px;
                    letter-spacing: 0;
                    line-height: 1.22;
                }
                .as-hero-section p {
                    margin-bottom: 18px;
                }
                .slideVideoScale {
                    transform: scale(1.345);
                }
            }
            @media (min-width: 1140px) {
                .as-hero-section .as-slide {
                    height: 576px;
                    padding: 0 60px;
                }
                .as-hero-section .slick-arrow.slick-prev {
                    left: 50px;
                }
                .as-hero-section .slick-arrow.slick-next {
                    right: 50px;
                }
            }
            @media (max-width: 1023px) {
                .as-hidden-sm {
                    display: none;
                }
            }
            @media (max-width: 767px) {
                .as-hero-section .slick-arrow.slick-next,
                .as-hero-section .slick-arrow.slick-prev {
                    top: 165px;
                }
            }
        `,
    },

    slideCenteringWrapperStyles: {
        css: css`
            width: 100%;
        `,
    },
    slideshowWrapper: {
        css: css`
            position: relative;
            height: 100%;
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
        `,
    },
    prevArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    iconProps: {
        size: 55,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: white;
            background-repeat: no-repeat;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
            display: inline-block;
            background: #f57e20;
            color: #fff;
            text-transform: uppercase;
            font-weight: 900;
            font-family:
                Inter Tight,
                Arial;
            line-height: 1;
            letter-spacing: 1.26px;
            text-align: center;
            font-size: 20px;
            padding: 16px 19px;
            min-width: 164px;
            border: 2px solid transparent;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            margin-top: -20px;
            position: absolute;
            width: 100%;
        `,
    },
    dotButton: {
        css: css`
            height: 12px !important;
            min-height: 12px !important;
            width: 12px !important;
            padding: 0;
            margin: 0 4px;
            border: 0;
            border-radius: 50%;
            background: ${getColor("primary.main")};
            &:hover,
            &:active {
                background: ${getColor("secondary.main")};
            }
            &:focus {
                outline: none;
            }
        `,
    },
    pausePlayDotButton: {
        css: css`
            cursor: pointer;
            height: 12px !important;
            min-height: 12px !important;
            width: 12px !important;
            padding: 0;
            margin: 0 4px;
            border: 0;
            background: transparent;
            display: flex;
            align-items: center;
            justify-content: center;
            &:hover {
                background: transparent;
            }
            &:focus {
                outline: none;
            }
            svg {
                width: 12px !important;
                height: 12px !important;
                min-height: 12px !important;
                color: ${getColor("secondary.main")};
            }
        `,
    },
    pausePlayDotButtonIcon: {
        css: css`
            position: static !important;
            top: auto !important;
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
            right: 0;
        `,
    },
    nextArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    headingText: {
        css: css`
            color: #fff;
            font-size: 28px;
            font-family:
                Inter Tight,
                Arial;
            line-height: 1;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 0.84px;
            margin: 0 0 12px;
        `,
    },
};

const defaultCustomCss = `.slideshow-wrapper {
}

.slideshow-carousel {
}

.slideshow-slide-container {
}

.slide-wrapper {
}

.slide-heading {

}

.slide-subheading {
}

.slide-button {
}

.slide-indicator {
}

.slide-dot-button {
}

.slide-arrow-wrapper {
}

.slide-arrow {
}

.slideshow-carousel,
.slideshow-slide-container,
.slide-wrapper {
  overflow: hidden;
  position: relative; /* slide-wrapper should already be relative from StyledWrapper, but confirm */
}
`;

export const Slideshow: FC<OwnProps> = ({ fields, extendedStyles }) => {
    let isMobile = false;

    if (typeof window !== "undefined" && window.innerWidth <= 768) {
        isMobile = true;
    }

    const history = useHistory();
    const buttonLinks = useGetLinks(fields.slides, o => o.fields.buttonLink);
    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);
    const { isInShell } = useShellContext();

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla || isPaused || !fields.slides || fields.slides.length <= 1) {
            return;
        }

        embla.scrollNext();
    }, [embla, isPaused, fields.slides]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: !!(fields.slides && fields.slides.length > 1),
        });
    }, [embla, fields.slides]);

    const onClick = (link: string | undefined) => {
        if (!link) {
            return;
        }

        if (link.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = link;
        } else {
            history.push(link);
        }
    };

    const handleSlideshowClick = (e: React.MouseEvent) => {
        if (!fields.pausable) {
            return;
        }

        const target = e.target as HTMLElement;
        const isInteractiveElement =
            target.tagName === "BUTTON" ||
            target.tagName === "A" ||
            target.closest("button") ||
            target.closest("a") ||
            target.classList.contains("slide-dot-button") ||
            target.closest(".slide-dot-button") ||
            target.classList.contains("pause-play-dot-button") ||
            target.closest(".pause-play-dot-button") ||
            target.classList.contains("slide-arrow") ||
            target.closest(".slide-arrow");

        if (!isInteractiveElement) {
            setIsPaused(!isPaused);
        }
    };

    const handlePausePlay = () => {
        setIsPaused(!isPaused);
    };

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }
    const textAlignStyles = `text-align: ${fields.textAlignment};`;

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="slideshow-wrapper" {...styles.asHeroSection} onClick={handleSlideshowClick}>
                {fields.showArrows && fields.slides.length > 1 && (
                    <StyledWrapper {...styles.prevArrowWrapper} style={{ left: `${isMobile ? 0 : 15}px` }}>
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            aria-label={translate("Previous")}
                        >
                            <ButtonIcon
                                style={{ color: `${fields.arrowColor}` }}
                                {...styles.iconProps}
                                src={ChevronLeft}
                            />
                        </Button>
                    </StyledWrapper>
                )}

                <StyledWrapper
                    className="slideshow-carousel as-hero-section"
                    {...styles.carouselContainer}
                    ref={emblaRef}
                >
                    <StyledWrapper
                        className="slideshow-slide-container as-hero-slider"
                        {...styles.slideContainerWrapper}
                    >
                        {fields.slides.map((slide, index) => {
                            const buttonLink = buttonLinks[index];

                            let responsiveImageBehaviorStyles;
                            if (slide.fields.responsiveImageBehavior === "cover") {
                                responsiveImageBehaviorStyles = "background-size: cover;";
                            } else if (slide.fields.responsiveImageBehavior === "center") {
                                responsiveImageBehaviorStyles = "background-size: contain;";
                            } else if (slide.fields.responsiveImageBehavior === "prioritizeHeight") {
                                responsiveImageBehaviorStyles = "background-size: auto 100%;";
                            } else if (slide.fields.responsiveImageBehavior === "prioritizeWidth") {
                                responsiveImageBehaviorStyles = "background-size: 100% auto;";
                            }

                            // DECIDE THE BACKGROUND IMAGE
                            let bgURL: string;
                            bgURL = "";
                            if (isMobile) {
                                bgURL = slide.fields.imageMobile;
                            } else {
                                bgURL = slide.fields.image;
                            }

                            const slideHeightCss =
                                typeof fields.slideHeight === "string" && fields.slideHeight.match(/^\d+$/)
                                    ? `${fields.slideHeight}px`
                                    : (fields.slideHeight as string | number);

                            let backgroundStyles = `height: ${slideHeightCss};`;

                            if (slide.fields.background === "image") {
                                backgroundStyles = `background-image: url(${bgURL}); height: ${slideHeightCss};`;
                            } else if (slide.fields.background === "color") {
                                backgroundStyles = `background-color: ${slide.fields.backgroundColor}; height: ${slideHeightCss};`;
                            } else if (slide.fields.background === "video") {
                                backgroundStyles = `background-color: transparent; height: ${slideHeightCss};`;
                            }

                            let overlayPositioningStyles;
                            if (!slide.fields.partialOverlay) {
                                overlayPositioningStyles = "align-items: stretch;";
                            } else if (slide.fields.partialOverlayPositioning === "top") {
                                overlayPositioningStyles = "align-items: flex-start;";
                            } else if (slide.fields.partialOverlayPositioning === "middle") {
                                overlayPositioningStyles = "align-items: center;";
                            } else if (slide.fields.partialOverlayPositioning === "bottom") {
                                overlayPositioningStyles = "align-items: flex-end;";
                            }

                            let fontSizeStyles;
                            if (fields.responsiveFontSizes || fields.customFontSizes) {
                                fontSizeStyles = responsiveStyleRules(
                                    fields.responsiveFontSizes,
                                    fields.customFontSizes ? fields : undefined,
                                );
                            }

                            const slideWrapperStyles = {
                                css: css`
                                    ${styles.slideContentWrapper?.css || ""}
                                    ${textAlignStyles}
                                    ${backgroundStyles}
                                    ${responsiveImageBehaviorStyles}
                                    ${overlayPositioningStyles}
                                    ${fontSizeStyles}
                                `,
                            };
                            const slideOverlayWrapperStyles = {
                                css: css`
                                    ${styles.slideOverlayWrapper?.css || ""}
                                    position: relative;
                                    z-index: 1;
                                    background-color: ${slide.fields.background === "image"
                                        ? slide.fields.imageOverlay
                                        : ""};
                                `,
                            };

                            let setAlign = "";
                            if (slide.fields.centerTextVertically === true) {
                                setAlign = "text-center";
                            } else if (
                                !slide.fields.centerTextVertically &&
                                slide.fields.contentAlignment === "right"
                            ) {
                                setAlign = "text-right";
                            }
                            const marginForDesktop = isMobile
                                ? {}
                                : slide.fields.centerTextVertically
                                  ? { marginTop: `${slide.fields.marginTop}px` }
                                  : {
                                        marginLeft: `${slide.fields.marginLeft}px`,
                                        marginTop: `${slide.fields.marginTop}px`,
                                    };

                            return (
                                <StyledWrapper className="slide-wrapper" key={index} {...slideWrapperStyles}>
                                    {slide.fields.background === "video" &&
                                        slide.fields.backgroundVideoURL &&
                                        (() => {
                                            const video = getVideoEmbed(slide.fields.backgroundVideoURL);
                                            const heightPx = slideHeightCss;

                                            if (video.type === "mp4") {
                                                return (
                                                    <div
                                                        style={{
                                                            position: "absolute",
                                                            inset: 0,
                                                            zIndex: -1,
                                                            height: heightPx as any,
                                                        }}
                                                    >
                                                        <video
                                                            className="slideVideoScale"
                                                            src={video.src}
                                                            width="100%"
                                                            height={heightPx as any}
                                                            autoPlay
                                                            muted
                                                            loop
                                                            playsInline
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "cover",
                                                                pointerEvents: "auto",
                                                                border: 0,
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            }

                                            if (
                                                video.type === "youtube" ||
                                                video.type === "vimeo" ||
                                                video.type === "unknown"
                                            ) {
                                                let iframeSrc = video.src;

                                                if (video.type === "youtube") {
                                                    const idMatch = iframeSrc.match(/embed\/([A-Za-z0-9_-]{5,})/);
                                                    const vidId = idMatch ? idMatch[1] : undefined;
                                                    const youtubeParams = `rel=0&modestbranding=1&autoplay=1&mute=1&controls=0&loop=1${vidId ? `&playlist=${vidId}` : ""}`;
                                                    iframeSrc =
                                                        iframeSrc +
                                                        (iframeSrc.includes("?") ? "&" : "?") +
                                                        youtubeParams;
                                                } else if (video.type === "vimeo") {
                                                    const vimeoParams = `autoplay=1&muted=1&background=1`;
                                                    iframeSrc =
                                                        iframeSrc + (iframeSrc.includes("?") ? "&" : "?") + vimeoParams;
                                                } else {
                                                    const fallbackParams = `autoplay=1&muted=1`;
                                                    iframeSrc =
                                                        iframeSrc +
                                                        (iframeSrc.includes("?") ? "&" : "?") +
                                                        fallbackParams;
                                                }

                                                iframeSrc = iframeSrc.replace(/&amp;/g, "&").replace(/&amp;amp;/g, "&");

                                                return (
                                                    <div
                                                        style={{
                                                            position: "absolute",
                                                            inset: 0,
                                                            zIndex: 0,
                                                            height: heightPx as any,
                                                            overflow: "hidden",
                                                        }}
                                                    >
                                                        <iframe
                                                            className="slideVideoScale"
                                                            src={iframeSrc}
                                                            width="100%"
                                                            height={heightPx as any}
                                                            frameBorder="0"
                                                            allow="autoplay; fullscreen; picture-in-picture"
                                                            allowFullScreen
                                                            title={`slide-video-${index}`}
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "cover",
                                                                pointerEvents: "auto",
                                                                border: 0,
                                                                background: "transparent",
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            }

                                            return null;
                                        })()}

                                    {slide.fields.background !== "video" && (
                                        <StyledWrapper {...slideOverlayWrapperStyles}>
                                            <div
                                                className={`as-slide ${setAlign}`}
                                                style={{ height: slideHeightCss as any }}
                                            >
                                                <div className="as-container">
                                                    <div className="as-text-area" style={marginForDesktop}>
                                                        <div className="as-text-box">
                                                            <h1
                                                                style={{
                                                                    color: `${
                                                                        isMobile
                                                                            ? slide.fields.headingMobileColor
                                                                            : slide.fields.headingColor
                                                                    }`,
                                                                    fontSize: isMobile
                                                                        ? slide.fields.mobileHeadingFontSize
                                                                        : slide.fields.headingFontSize,
                                                                }}
                                                            >
                                                                {isMobile ? (
                                                                    <>
                                                                        {slide.fields.headingMobile ? (
                                                                            <>
                                                                                {parse(
                                                                                    slide.fields.headingMobile,
                                                                                    parserOptions,
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                {parse(
                                                                                    slide.fields.heading,
                                                                                    parserOptions,
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {slide.fields.heading && (
                                                                            <>
                                                                                {parse(
                                                                                    slide.fields.heading,
                                                                                    parserOptions,
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </h1>
                                                            <p
                                                                style={{
                                                                    color: `${
                                                                        isMobile
                                                                            ? slide.fields.subheadingMobileColor
                                                                            : slide.fields.subheadingColor
                                                                    }`,
                                                                    fontSize: isMobile
                                                                        ? slide.fields.mobileSubHeadingFontSize
                                                                        : slide.fields.subHeadingFontSize,
                                                                }}
                                                            >
                                                                {isMobile ? (
                                                                    <>
                                                                        {slide.fields.subheadingMobile ? (
                                                                            <>
                                                                                {parse(
                                                                                    slide.fields.subheadingMobile,
                                                                                    parserOptions,
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                {parse(
                                                                                    slide.fields.subheading,
                                                                                    parserOptions,
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {slide.fields.subheading && (
                                                                            <>
                                                                                {parse(
                                                                                    slide.fields.subheading,
                                                                                    parserOptions,
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </p>
                                                            {(slide.fields.buttonLabel ||
                                                                slide.fields.buttonLink.value) && (
                                                                <Button
                                                                    className="as-btn"
                                                                    variant={slide.fields.buttonVariant}
                                                                    onClick={() => onClick(buttonLink?.url)}
                                                                >
                                                                    {slide.fields.buttonLabel ||
                                                                        buttonLink?.title ||
                                                                        buttonLink?.url}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <StyledWrapper {...styles.slideCenteringWrapperStyles}></StyledWrapper>
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                </StyledWrapper>
                {fields.slideIndicator && (
                    <StyledWrapper className="slide-indicator" {...styles.dotsContainerWrapper}>
                        {fields.slides.map((_, i) => {
                            const dotColorStyles =
                                i === selectedIndex
                                    ? `
                                       background: ${getColor("secondary.main")};
                                            &:hover { background: ${getColor("secondary.main")} }
                                    `
                                    : "";
                            const dotButtonStyles = {
                                css: css`
                                    ${styles.dotButton?.css || ""}
                                    ${dotColorStyles}
                                `,
                            };

                            return (
                                <Button
                                    className="slide-dot-button"
                                    key={`slide-dot-${i}`}
                                    {...dotButtonStyles}
                                    onClick={() => embla && embla.scrollTo(i)}
                                    aria-label={translate("Scroll to {0}", `${i + 1}`)}
                                />
                            );
                        })}
                        {fields.pausable && (
                            <Button
                                className="pause-play-dot-button"
                                {...styles.pausePlayDotButton}
                                onClick={handlePausePlay}
                                aria-label={translate(isPaused ? "Play slideshow" : "Pause slideshow")}
                            >
                                <ButtonIcon src={isPaused ? Play : Pause} {...styles.pausePlayDotButtonIcon} />
                            </Button>
                        )}
                    </StyledWrapper>
                )}
                {fields.showArrows && fields.slides.length > 1 && (
                    <StyledWrapper
                        className="slide-arrow-wrapper"
                        style={{ right: `${isMobile ? 0 : 15}px` }}
                        {...styles.nextArrowWrapper}
                    >
                        <Button
                            className="slide-arrow"
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            aria-label={translate("Next")}
                        >
                            <ButtonIcon
                                style={{ color: `${fields.arrowColor}` }}
                                {...styles.iconProps}
                                src={ChevronRight}
                            />
                        </Button>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: Slideshow,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.slideHeight,
                displayName: "Slide Height (in pixels)",
                editorTemplate: "TextField",
                defaultValue: "576",
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: "arrowColor",
                editorTemplate: "ColorPickerField",
                displayName: "arrow Color",
                tab: settingsTab,
                fieldType: "General",
                defaultValue: "",
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.pausable,
                displayName: "Pausable",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                            { displayName: "Video", value: "video" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "imageMobile",
                        displayName: "Image for Mobile",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "responsiveImageBehavior",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Cover", value: "cover" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Prioritize height", value: "prioritizeHeight" },
                            { displayName: "Prioritize width", value: "prioritizeWidth" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "cover",
                    },
                    {
                        name: "backgroundVideoURL",
                        displayName: "Embed Video Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "video",
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Color",
                        defaultValue: "black",
                        isVisible: widget => widget.fields.background === "color",
                    },
                    {
                        name: "heading",
                        editorTemplate: "TextField",
                        displayName: "Heading",
                        defaultValue: "",
                    },
                    {
                        name: "headingColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Heading text Color",
                        defaultValue: "#013d54",
                    },
                    {
                        name: "headingMobile",
                        editorTemplate: "TextField",
                        displayName: "Heading Mobile",
                        defaultValue: "",
                    },
                    {
                        name: "headingMobileColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Heading Mobile text color",
                        defaultValue: "",
                    },
                    {
                        name: fields.headingFontSize,
                        editorTemplate: "IntegerField",
                        min: 1,
                        defaultValue: 32,
                    },
                    {
                        name: fields.subHeadingFontSize,
                        editorTemplate: "IntegerField",
                        min: 1,
                        defaultValue: 18,
                    },
                    {
                        name: fields.mobileHeadingFontSize,
                        editorTemplate: "IntegerField",
                        min: 1,
                        defaultValue: 28,
                    },
                    {
                        name: fields.mobileSubHeadingFontSize,
                        editorTemplate: "IntegerField",
                        min: 1,
                        defaultValue: 18,
                    },
                    {
                        name: "subheading",
                        editorTemplate: "TextField",
                        displayName: "Subheading",
                        defaultValue: "",
                    },
                    {
                        name: "subheadingColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Subheading text Color",
                        defaultValue: "#013d54",
                    },
                    {
                        name: "subheadingMobile",
                        editorTemplate: "TextField",
                        displayName: "Subheading Mobile",
                        defaultValue: "",
                    },
                    {
                        name: "subheadingMobileColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Sub Heading Mobile text color",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "buttonVariant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                    {
                        name: "marginTop",
                        displayName: "Top Margin (Content)",
                        editorTemplate: "IntegerField",
                        defaultValue: 10,
                    },
                    {
                        name: "marginLeft",
                        displayName: "Left Margin (Content)",
                        editorTemplate: "IntegerField",
                        defaultValue: 10,
                        isVisible: widget => widget.fields.centerTextVertically === false,
                    },
                    {
                        name: "contentAlignment",
                        displayName: "Content Alignment",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Left", value: "left" },
                            { displayName: "Right", value: "right" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "left",
                        isVisible: widget => widget.fields.centerTextVertically === false,
                    },
                    {
                        name: "centerTextVertically",
                        displayName: "Center Content",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
